import clsx from 'clsx';
import React from 'react';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { AdvancedParameters } from '@/core/AdvancedParameters.atom';
import { FakeLink } from '@/core/FakeLink';
import { Badge, Button, Icon } from '@seeqdev/qomponents';

interface FormConditionProps {
  conditionName: string;
  children: React.ReactNode;
  show?: boolean;
}

export const FormCondition = ({ conditionName, children, show }: FormConditionProps) => (
  <Field name={conditionName} subscription={{ value: true }} key={`${conditionName}_field`}>
    {() => (show ? children : null)}
  </Field>
);

interface ConditionalFormBlockProps {
  isConditional?: boolean;
  show?: boolean;
  name: string;
  children: React.ReactNode;
}

export const ConditionalFormBlock = ({ isConditional, show, name, children }: ConditionalFormBlockProps) => {
  if (isConditional) {
    return (
      <FormCondition conditionName={`${name}_condition`} show={show} key={`${name}_condition`}>
        {children}
      </FormCondition>
    );
  }
  return <React.Fragment key={`${name}_conditionBlock`}>{children}</React.Fragment>;
};

interface FormBlockProps {
  name: string;
  children: React.ReactNode;
  formBlockClassNames?: string;
}

export const FormBlock = ({ name, children, formBlockClassNames }: FormBlockProps) => {
  return (
    <div className={clsx('flexColumnContainer formElement', formBlockClassNames)} key={name} id={`${name}-wrapper`}>
      {children}
    </div>
  );
};

interface FormRowBlockProps {
  name: string;
  testId?: string;
  extraClassNames?: string;
  children: React.ReactNode;
}

export const FormRowBlock = ({ name, testId, extraClassNames, children }: FormRowBlockProps) => (
  <div className={clsx('flexColumnContainer', extraClassNames)} key={`${name}_row`} id={name} data-testid={testId}>
    {children}
  </div>
);

interface TitledFormRowBlockProps {
  name: string;
  testId?: string;
  extraClassNames?: string;
  wrapperClassNames?: string;
  title: string;
  helpText?: string;
  children: React.ReactNode;
}

export const TitledFormRowBlock = ({
  name,
  testId,
  extraClassNames,
  title,
  helpText,
  children,
  wrapperClassNames,
}: TitledFormRowBlockProps) => {
  const { t } = useTranslation();

  return (
    <div className={clsx(wrapperClassNames)} key={`${name}_row`} id={name} data-testid={testId}>
      <div className="tw:block tw:mb-[10px] tw:px-3 tw:pt-1 tw:pb-1 tw:w-full formGroupTitle">{t(title)}</div>
      {helpText && <div className="tw:px-3 tw:pb-2 tw:italic tw:text-sm tw:text-center">{t(helpText)}</div>}
      <div className={clsx('flexColumnContainer', extraClassNames)}>{children}</div>
    </div>
  );
};

interface RemovableFormRowBlockProps {
  name: string;
  testId?: string;
  extraClassNames?: string;
  wrapperClassNames?: string;
  children: React.ReactNode;
  hideRemoveIcon?: boolean;
  removeAction: () => void;
}

export const RemovableFormRowBlock = ({
  name,
  testId,
  extraClassNames,
  wrapperClassNames,
  children,
  hideRemoveIcon,
  removeAction,
}: RemovableFormRowBlockProps) => (
  <div className={clsx('removableRowWrapper', wrapperClassNames)} data-testid={`${testId}_wrapper`}>
    {!hideRemoveIcon && (
      <Icon icon="fa-close" testId="removeIcon" extraClassNames="removeIcon" onClick={removeAction} />
    )}
    <div className={clsx('flexColumnContainer', extraClassNames)} key={`${name}_row`} id={name} data-testid={testId}>
      {children}
    </div>
  </div>
);

interface AdvancedFormBlockProps {
  name: string;
  testId?: string;
  toolName: string;
  toolId: string;
  alternateName?: string;
  extraClassNames?: string;
  iconStyle?: string;
  collapsed: boolean;
  onToggle?: (collapsed: boolean) => void;
  children: React.ReactNode;
}

export const AdvancedFormBlock = ({
  name,
  testId,
  toolName,
  toolId,
  alternateName,
  extraClassNames,
  iconStyle,
  collapsed,
  onToggle,
  children,
}: AdvancedFormBlockProps) => (
  <div
    className={clsx('flexRowContainer advancedPanel toolOptions mb15', extraClassNames)}
    key={`${name}_wrapper`}
    data-testid={testId}
    id={name}>
    <AdvancedParameters
      toolName={toolName}
      toolId={toolId}
      alternateName={alternateName}
      iconStyle={iconStyle}
      collapsed={collapsed}
      onToggle={onToggle}>
      {children}
    </AdvancedParameters>
  </div>
);

export const FormGroupBlock = ({
  name,
  testId,
  formGroupExtraClassNames,
  showBracket,
  children,
  showScroll,
  showGroupStyles,
}: {
  name: string;
  testId?: string;
  formGroupExtraClassNames?: string;
  showBracket?: boolean;
  children: React.ReactNode;
  showScroll?: boolean;
  showGroupStyles?: boolean;
}) => (
  <div
    className={clsx('flexRowContainer', showScroll ? 'flexFill' : 'flexFillOverflow', formGroupExtraClassNames, {
      leftBracket: showBracket,
      groupBlockContent: showGroupStyles,
    })}
    key={`${name}_wrapper`}
    data-testid={testId || name}
    id={name}>
    {children}
  </div>
);

interface FormGroupWithTitleBlockProps {
  name: string;
  testId?: string;
  extraClassNames?: string;
  showBracket?: boolean;
  children: React.ReactNode;
  showScroll?: boolean;
  showTitle?: boolean;
  title?: string;
  titleLink?: string;
  titleLinkAction?: () => void;
  additionalTitleLink?: string;
  additionalTitleLinkAction?: () => void;
}

export const FormGroupWithTitleBlock = ({
  name,
  testId,
  extraClassNames,
  showBracket,
  children,
  showScroll,
  showTitle,
  title,
  titleLink,
  titleLinkAction,
  additionalTitleLink,
  additionalTitleLinkAction,
}: FormGroupWithTitleBlockProps) => (
  <div
    className={clsx('flexRowContainer', showScroll ? 'flexFill' : 'flexFillOverflow', extraClassNames, {
      leftBracket: showBracket,
    })}
    key={`${name}_wrapper`}
    data-testid={testId || name}
    id={name}>
    {showTitle && (
      <div className="formGroupTitle">
        <span className="formGroupTitleText">{title}</span>
        <div className="formGroupTitleActions">
          {additionalTitleLink && (
            <FakeLink onClick={additionalTitleLinkAction} extraClassNames="formGroupTitleLink link-underline-hover">
              {additionalTitleLink}
            </FakeLink>
          )}
          {titleLink && (
            <FakeLink onClick={titleLinkAction} extraClassNames="formGroupTitleLink link-underline-hover">
              {titleLink}
            </FakeLink>
          )}
        </div>
      </div>
    )}
    {children}
  </div>
);

interface RemovableFormBlockProps {
  testId?: string;
  children: React.ReactNode;
  iconAction?: () => void;
  hideIcon?: boolean;
  extraClassNames?: string;
  iconHasMargin?: boolean;
  iconTooltip?: string;
  showLabelAfterRows?: boolean;
  labelAfterRows?: string;
  addBlockButtonAction?: () => void;
  addBlockButtonTooltip?: string;
  showAddBlockButton?: boolean;
}

export const RemovableFormBlock = ({
  testId,
  children,
  iconAction,
  hideIcon,
  extraClassNames,
  iconHasMargin,
  iconTooltip,
  showLabelAfterRows,
  labelAfterRows,
  addBlockButtonAction,
  addBlockButtonTooltip,
  showAddBlockButton,
}: RemovableFormBlockProps) => {
  const icon = !hideIcon && (
    <div
      onClick={iconAction}
      className={clsx('cursorPointer', iconHasMargin ? 'closeIconWrapperMargin' : 'closeIconWrapper')}>
      <Icon icon="fa-close" testId="deleteIcon" tooltip={iconTooltip} />
    </div>
  );

  const wrapperClass = clsx('removableBlockWrapper', extraClassNames);
  const labelClasses = 'addFormBlockLabel rightAlignedLabel';

  return iconHasMargin ? (
    <div className="inputContainer">
      {icon}
      <div className={wrapperClass}>{children}</div>
      {showLabelAfterRows && <div className={labelClasses}>{labelAfterRows}</div>}
      {showAddBlockButton && (
        <div className={labelClasses}>
          <Button
            testId="addBlockButton"
            variant="theme"
            onClick={addBlockButtonAction}
            icon="fa-plus"
            iconStyle="white"
            size="sm"
            tooltip={addBlockButtonTooltip}
            extraClassNames="width-35"
          />
        </div>
      )}
    </div>
  ) : (
    <div className={wrapperClass} data-testid={testId}>
      {icon}
      <>{children}</>
    </div>
  );
};

export interface CollapsibleFormGroupProps {
  testId?: string;
  children: React.ReactNode;
  deleteIconAction: () => void;
  hideDeleteIcon?: boolean;
  additionalIcon?: string;
  additionalIconAction?: () => void;
  additionalIconTooltip?: string;
  extraClassNames?: string;
  labelExtraClassNames?: string;
  isCollapsed: boolean;
  toggleCollapse: () => void;
  label: string;
  isLast?: boolean;
  isNewlyAdded?: boolean;
  newLabel?: string;
  dragRef?: React.Ref<HTMLDivElement>;
  isDragging?: boolean;
  dragHandleTooltip?: string;
}

export const CollapsibleFormBlock = ({
  testId,
  children,
  deleteIconAction,
  hideDeleteIcon,
  additionalIcon,
  additionalIconAction,
  additionalIconTooltip,
  extraClassNames,
  labelExtraClassNames,
  isCollapsed,
  toggleCollapse,
  label,
  isLast,
  isNewlyAdded,
  newLabel,
  dragRef,
  isDragging,
  dragHandleTooltip,
}: CollapsibleFormGroupProps) => {
  const { t } = useTranslation();

  const icon = (
    <div className="cursorPointer closeIconWrapper">
      <Icon onClick={toggleCollapse} icon={isCollapsed ? 'fa-chevron-right' : 'fa-angle-down'} testId="toggleIcon" />
      {dragRef ? (
        <Icon icon="fa-arrows" extraClassNames="ml10 dragHandle" testId="dragHandle" tooltip={dragHandleTooltip} />
      ) : null}
      {additionalIcon && additionalIconAction ? (
        <Icon
          onClick={additionalIconAction}
          icon={`${additionalIcon} tw:ml-[10px]`}
          testId="additionalIcon"
          tooltip={additionalIconTooltip ? t(additionalIconTooltip) : undefined}
        />
      ) : null}
      {hideDeleteIcon ? null : <Icon onClick={deleteIconAction} icon="fa-close ml10" testId="deleteIcon" />}
    </div>
  );
  const newBadge = <Badge extraClassNames="ml7">{newLabel}</Badge>;

  return (
    <div
      ref={dragRef}
      className={clsx('collapsibleBlockWrapper grayBorder', extraClassNames, {
        bottomBorderRadius: isLast,
      })}
      data-testid={testId}
      style={dragRef ? { opacity: isDragging ? 0.1 : 1 } : undefined}>
      {icon}
      <div className={clsx('text-bold', labelExtraClassNames, { mb10: !isCollapsed })} onClick={toggleCollapse}>
        <span className={clsx('text-underline cursorPointer')}>{label}</span>
        {isNewlyAdded ? newBadge : null}
      </div>
      <div className={isCollapsed ? 'collapsibleContent' : 'expandedCollapsibleContent'}>{children}</div>
    </div>
  );
};

interface DisplayOnlyFormElementWrapperProps {
  extraClassNames?: string;
  testId?: string;
  children: React.ReactNode;
}

export const DisplayOnlyFormElementWrapper = ({
  extraClassNames,
  testId,
  children,
}: DisplayOnlyFormElementWrapperProps) => (
  <div className={clsx(extraClassNames)} data-testid={testId}>
    {children}
  </div>
);
